import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Popover,
    Stack,
    Typography
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from "@react-keycloak/web";
import axios from "axios";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { each } from "lodash";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { createEditor, Editor, Node, Range, Text, Transforms } from "slate";
import {
    Editable,
    ReactEditor,
    Slate,
    useSlate,
    useSlateSelection,
    withReact,
} from "slate-react";
import { v4 as uuidv4 } from "uuid";
import {
    baseAtom,
    getAssemblyTypeFromInterview,
    selectInsight
} from "../../../stores/jotai/base";
import {
    interviewAtom,
    setupLibrary,
    setupLibraryAndDictionary,
} from "../../../stores/jotai/interviews/currentInterview";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { findingsAtom } from "../../../stores/jotai/insights/findings";
import { interviewFindingsAtom } from "../../../stores/jotai/insights/interviewFindings";
import { migrationStateAtom } from "../../../stores/jotai/migration/migration";
import { protectedApiCall } from "../../../api/network";

import { setIsTranscriptLoadedFromPubnub, transcriptStateAtom } from "../../../stores/jotai/transcript/transcript";
// import { useFetchTranscriptData } from "../../../tanstackQuery/queries/editor";
import { useGetFindingArtifact } from "../../../tanstackQuery/queries/findings/useGetFindingArtifact";
import { appLog } from "../../../util";
import { updateTranscriptData } from "../../../util/transcriptionUpdateUtils";
import IwButton from "../../app/IwButton";
import MaterialSpeakerPopup from "./material-speaker-popup";
import MaterialTranscriptionAudioPlayer from "./material-transcription-audio-player";
import TranscriptionEditorHeader from "./TranscriptionEditorHeader";
import TranscriptSpeakerElement from "./TranscriptSpeakerElement";
import { reTranscriptionProgressAtom, setRetrancriptionProgressByInterviewID } from "../../../stores/jotai/transcript/reTranscriptionProgress";
import { useQueryClient } from "@tanstack/react-query";

function generateInitialValue(transcript: any, cb: any) {
    if (transcript === undefined) return [];

    let speakerDictionaryBuffer = [];
    let speakersFound = ["spk_0"];
    let initialValue: any = [];

    speakerDictionaryBuffer.push({
        documentID: uuidv4(),
        speakerID: "spk_0"
    })

    
    if (transcript === "") {
        return []
    }

    let lastVisibleSpeaker = "spk_0";
    let tokens = [];

    if (transcript?.utterances === undefined) return {}

    for (let i = 0; i < transcript?.utterances?.length; i++) {
        const { words } = transcript.utterances[i];

        for (let w = 0; w < words?.length; w++) {
            tokens.push(words[w]);

            const sKey = "spk_" + words[w]?.speaker.toString()

            if (speakersFound.includes(sKey) === false) {
                speakersFound.push(sKey);
            }
        }
    }

    initialValue.push({
        type: 'speaker',
        person: speakerDictionaryBuffer?.[0]?.documentID,
        time: 0,
        children: [{ text: "" }],
    })

    // @ts-ignore
    tokens = tokens.sort((a: any, b: any) => {
        const aTime = parseFloat(a.start);
        const bTime = parseFloat(b.start);

        if (aTime === bTime) {
            return -1;
        }
        else {
            return aTime < bTime;
        }
    })

    let wordsSince = [];
    for (let i = 0; i < tokens?.length; i++) {
        const time = tokens[i]?.start;
        const content = tokens[i]?.punctuated_word + " ";
        const speaker = tokens[i]?.speaker;
        const sKey = "spk_" + speaker.toString()

        if (sKey !== lastVisibleSpeaker) {
            lastVisibleSpeaker = sKey;

            initialValue.push({
                type: 'paragraph',
                children: wordsSince
            });

            const anotherD = uuidv4();
            speakerDictionaryBuffer.push({
                documentID: anotherD,
                speakerID: sKey
            })

            initialValue.push({
                type: 'speaker',
                person: anotherD,
                time: time,
                children: [{ text: "" }],
            })

            wordsSince = [];
        }

        wordsSince.push({
            text: content,
            id: uuidv4(),
            timeStamp: time,
            confidence: tokens[i]?.confidence
        })
    }

    if (wordsSince?.length > 0) {
        initialValue.push({
            type: 'paragraph',
            children: wordsSince
        });
    }

    let buffer = [];

    for (let i = 0; i < speakersFound?.length; i++) {
        buffer.push({
            name: speakersFound[i],
            id: speakersFound[i]
        })
    }

    cb(speakerDictionaryBuffer, buffer, initialValue);

    return initialValue;
}

function getBlockFromLocation(editor, location) {
    const selectedTextBlock = location?.path[1];

    const children = editor?.children?.[location?.path[0]];

    if (children !== undefined) {
        return children.children?.[selectedTextBlock];
    }
    else {
        return undefined;
    }
}

const getSelectedTextNodes = (editorInstance) => {
    const nodes = [];
    const fragments = editorInstance.getFragment(editorInstance.selection);

    fragments?.forEach(fragment => {
        fragment?.children?.forEach(child => {
            if (
                !!child.text
            ) {
                nodes.push(child);
            }
        });
    });

    return nodes;
}

function getSelectedText(selectedTextNodes = []) {
    return selectedTextNodes.map((node) => node?.text || "").join(" ");
}

const SelectionMonitor = (props) => {
    const editor = useSlate();
    const selection = useSlateSelection();
    const [base, setBase] = useAtom(baseAtom);
  
    const lastTextCacheRef = useRef("");
    const lastSelectionRef = useRef(null);
  
    useEffect(() => {
      if (!selection || Range.isCollapsed(selection)) return;
  
      const selectedTextNodes = getSelectedTextNodes(editor);
      const text = getSelectedText(selectedTextNodes);
  
      if (!text) return;
  
      const normalizedSelection = {
        anchor: Range.isBackward(selection) ? selection.focus : selection.anchor,
        focus: Range.isBackward(selection) ? selection.anchor : selection.focus,
      };
  
      const result = {
        selection: normalizedSelection,
        textCache: text,
        source: "transcript",
        audioBegin: selectedTextNodes[0]?.timeStamp,
        audioEnd: selectedTextNodes.at(-1)?.timeStamp,
      };
  
      const selectionChanged =
        lastTextCacheRef.current !== text ||
        JSON.stringify(normalizedSelection) !== JSON.stringify(lastSelectionRef.current);
  
      if (selectionChanged) {
        lastTextCacheRef.current = text;
        lastSelectionRef.current = normalizedSelection;
  
        window.__lastInsightSelection = result;

        if (base.selectedFinding !== undefined) {
          selectInsight(setBase, undefined);
        }
  
        if (props.onSet) {
          props.onSet(selection);
        }
      }
    }, [selection, base.selectedFinding]);
  
    return null;
  };
  
 const renderVideoBlock = (assemblies,selectedInterview) => {
    // only render when we have a video attached
    const type = getAssemblyTypeFromInterview(assemblies, selectedInterview);

    // if (selectedArtifactType === "notes") {
    //   return undefined;
    // }

    if (type === "video") {
      const baseHeight = 720;
      const baseWidth = 1280;
      const previewScale = 0.3;

      const stillProcessing = false;

      return (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={0}
          sx={{
            background: "black",
            width: "100%",
            // borderRadius: "4px 4px 0px 0px",
            marginTop: "5 px"
          }}
        >
          {stillProcessing ? (
            <div
              style={{
                transform: "none",
                backgroundColor: "white",
                height: `${baseHeight * previewScale}px`,
                width: `${baseWidth * previewScale}px`
              }}
              id="video-render-target-from-app"
            />
          ) : (
            <div
              style={{
                transform: "none",
                backgroundColor: "white",
                height: `${baseHeight * previewScale}px`,
                width: `${baseWidth * previewScale}px`
              }}
              id="video-render-target-from-app"
            />
          )}
        </Stack>
      );
    }

    return undefined;
  };

  const FetchingTranscriptLoader = ({theme,noBorder=false}) => (
        <Stack>
            <Alert
                severity="info"
                sx={{ border: !noBorder && `1px solid ${theme.custom.borderGrey}` }}
            >
                <Stack spacing={2}>
                    <AlertTitle>Fetching Transcription</AlertTitle>
                    <Typography sx={{ fontWeight: 400 }}>
                        Fetching transcription for this interview. Please,
                        wait.
                    </Typography>
                    <LinearProgress />
                </Stack>
            </Alert>
        </Stack>
    );

export default function MaterialTranscriptEditor(props) {
     const {data:transcriptionValue, isFetchingTranscriptionData, isError, refetch}=props;
    // console.log({data, isFetchingTranscriptionData})
    const [audioLeafFlag, setAudioLeafFlag] = useState(undefined);
    const [editor] = useState(() => withReact(createEditor()));
    const [selectedSpeaker, setSelectedSpeaker] = useState("");
    const [analysis, setAnalysis] = useState(undefined);
    const [transcriptData, setTranscriptData] = useState(undefined);
    const [audioTime, setAudioTime] = useState(0.0);
    const [assemblyStatus, setAssemblyStatus] = useState(true);
    const [initialEditorValue, setInitialEditorValue] = useState([]);
    const [lastSeenSelection, setLastSeenSelection] = useState(undefined);
    const { keycloak, initialized } = useKeycloak()
    const [base, setBase] = useAtom(baseAtom);
    const [
        transcriptAtom,
        setTranscriptAtom,
    ] = useAtom(transcriptStateAtom);
    const [interviewState, setInterviewState] = useAtom(interviewAtom);
    const [migrationState, setMigrationState] = useAtom(migrationStateAtom);
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
    const setRetranscriptionProgress = useSetAtom(reTranscriptionProgressAtom);
    const reTranscriptionProgress = useAtomValue(reTranscriptionProgressAtom);
    const [isReTranscribing, setIsReTranscribing] = useState(false);
    const [
        showTranscriptConfirmationModal,
        setShowTranscriptConfirmationModal,
    ] = useState(!props.hasTranscript);
    const [listenWait, setListenWait] = useState(true);
    const outerEditorPortal = useRef(null);
    const [newSpeakerRequest, setNewSpeakerRequest] = useState(undefined);
    const [audioBridge, setAudioBridge] = useState(undefined);
    const [transcriptTimeDisplay, setTranscriptTimeDisplay] = useState(0);
    const [transcriptTimeDuration, setTranscriptTimeDuration] = useState(60);
    const [currentActiveTranscriptionWordID, setCurrentActiveTranscriptionWordID] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showEditorClickBlocker, setShowEditorClickBlocker] = useState(false);
    const [tSaveCache, setTSaveCache] = useState(undefined);
    const [lSaveCache, setLSaveCache] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [showPlayers, setShowPlayers] = useState(false);
    const { speakerLibrary, speakerDictionary } = interviewState;
    const speakerRequest = newSpeakerRequest;
    const isReadOnly = !isEditing;
    const speakerElements = transcriptData?.data?.slate?.filter(
        (element) => element?.type === "speaker"
    );
    const client = useQueryClient();
    const speakerWithTimeRange = {};
    // @ts-ignore
    const transcriptionBasePlayer = window?.bplayer;
    const {transcriptDataURL, customFindingTypes} = props; 
    // const teamID = base?.team?.groupID; 
    // const keycloakToken = keycloak?.token;
    const isTranscriptLoadedFromPubnub = transcriptAtom?.isTranscriptLoadedFromPubnub;
    const [transcriptHasError, setTranscriptHasError] = useState<boolean>(false);
    const [isDecorateComplete, setIsDecorateComplete] = useState<boolean>(false);
    const [interviewFindings,]=useAtom(interviewFindingsAtom);
    const [findings,]=useAtom(findingsAtom);
    const theme = useTheme();
    const siev = (data) => {
        setInitialEditorValue(data);
    };
    const appHeaderHeight = "78px";
    const transcriptSectionHeaderHeight = "48px";

    useEffect(() => {
        setTranscriptHasError(false);
        setTranscriptData(undefined);
        setInitialEditorValue([]);
        setIsDecorateComplete(false);
        setLoadedTranscriptID(null);
    }, [base?.selectedInterview]);

    useEffect(() => {
        const timer = setTimeout(() => setShowPlayers(true), 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        setListenWait(true);
    }, [])

    const removePausedHighlighter = () => {
        const allPausedNodes = window.document.querySelectorAll(
            ".TranscriptionEditor .current-active-word--paused"
        );

        if(allPausedNodes){
            allPausedNodes?.forEach((node) => {
                node.classList.remove("current-active-word--paused");
            });
        }
    }

    const getCurrentlyPlayingDocumentID = () => {
        let currentlyPlaying = null;

        if (transcriptTimeDisplay < 0.1) {
            return null;
        }

        Object.keys(speakerWithTimeRange)?.some((documentID) => {
            const range = speakerWithTimeRange?.[documentID];

            if (
                transcriptTimeDisplay >= range?.startTime &&
                transcriptTimeDisplay < range?.stopTime
            ) {
                currentlyPlaying = documentID;

                return true;
            }

            return false;
        });

        return currentlyPlaying;
    }

    const removeTalkingSpeakerStyle = (currentlyPlaying) => {
        const inactiveSpeakerElements = window.document.querySelectorAll(
            `.TranscriptionEditor .transcript-editor-speaker-element--talking:not(#speaker-document-${currentlyPlaying})`
        );

        if(inactiveSpeakerElements){
            inactiveSpeakerElements?.forEach((element) => {
                element.classList.remove("transcript-editor-speaker-element--talking");
            });
        }
    };

    const removeActiveSpeakerStyle = (currentlyPlaying) => {
        const inactiveSpeakerElements = window.document.querySelectorAll(
            `.TranscriptionEditor .transcript-editor-speaker-element--active:not(#speaker-document-${currentlyPlaying})`
        );

        if(inactiveSpeakerElements){
            inactiveSpeakerElements?.forEach((element) => {
                element.classList.remove("transcript-editor-speaker-element--active");
            });
        }

        removeTalkingSpeakerStyle(currentlyPlaying);
    };
    
    const getSpeakerFromDocID = (id: string) => {
        const selectedNode = speakerLibrary?.find(
            (node) => node.documentID === id
        );
        const selectedSpeaker = speakerDictionary?.find(
            (speaker) => speaker?.id === selectedNode?.speakerID
        );

        return selectedSpeaker;
    }

    const navigateToActiveWord = () => {
        // @ts-ignore
        const activeWordID = window?.bCurrentWordFlag;
        const activeWordSource = window.document.querySelector(
            ".TranscriptionEditor #leaf-" + activeWordID
        );

        if (!!activeWordSource) {
            activeWordSource.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }

    const removeEmptyStringNodes = () => {
        const emptyStringNodes = window.document.querySelectorAll(
            ".TranscriptionEditor span[data-slate-zero-width]"
        );

        if(emptyStringNodes){
            emptyStringNodes?.forEach((source) => {
                if (!!source) {
                    source.parentElement.remove();
                }
            });
        }
    }

    const removeMarkerFromInactiveWords = () => {
        const allActiveSources = window.document.querySelectorAll(
            ".TranscriptionEditor .current-active-word"
        );

        if(allActiveSources){
            allActiveSources?.forEach((prevActiveSource) => {
                if (!!prevActiveSource) {
                    prevActiveSource.classList.remove("current-active-word");
                }
            });
        }
    };

    useHotkeys(
        "ctrl+l",
        (event) => {
            event.preventDefault();
            navigateToActiveWord();
        },
        { enableOnFormTags: true, enableOnContentEditable: true }
    );

    useHotkeys("escape", (event) => {
        if (!!lastSeenSelection) {
            clearEditorSelection();
        }
    })

    useEffect(() => {
        speakerElements?.forEach((element, index) => {
            speakerWithTimeRange[element?.person] = {
                startTime: element?.time,
                stopTime:
                    index === speakerElements?.length - 1
                        ? transcriptTimeDuration
                        : speakerElements[index + 1]?.time,
            };
        });
    }, [speakerElements]);

    useEffect(() => {
        const currentlyPlaying = getCurrentlyPlayingDocumentID();

        // @ts-ignore
        window.currentlyPlayingSpeakerDocumentID = currentlyPlaying;

        if (!!currentlyPlaying) {
            const activeSpeakerElement = window.document.querySelector(
                `.TranscriptionEditor #speaker-document-${currentlyPlaying}`
            );

            removeActiveSpeakerStyle(currentlyPlaying);

            if (
                !!activeSpeakerElement &&
                transcriptionBasePlayer?.isReady() &&
                transcriptionBasePlayer?.isPaused() === false
            ) {
                activeSpeakerElement.classList.add(
                    "transcript-editor-speaker-element--active",
                    "transcript-editor-speaker-element--talking"
                );
            }
        }
    }, [speakerElements, transcriptTimeDisplay]);

    useEffect(() => {
        setCurrentActiveTranscriptionWordID(() => {
            // @ts-ignore
            return window.bCurrentWordFlag;
        });

        removeActiveSpeakerStyle(null);
    }, [])

    useEffect(() => {
        const timeupdateHandler = () => {
            setCurrentActiveTranscriptionWordID(() => {
                // @ts-ignore
                return window.bCurrentWordFlag;
            });
        };
        const intervalID = setInterval(() => {
            timeupdateHandler()
        }, 50);

        return () => {
            clearInterval(intervalID);
        }
    }, []);

    useEffect(() => {
        removeEmptyStringNodes();
        removePausedHighlighter();
        removeMarkerFromInactiveWords();

        const currentSources = window.document.querySelectorAll(
            ".TranscriptionEditor #leaf-" + currentActiveTranscriptionWordID
        );

        if(currentSources){
            currentSources?.forEach((currentSource) => {
                if (
                    transcriptionBasePlayer?.isReady() &&
                    !!currentSource &&
                    !currentSource.querySelector("span[data-slate-zero-width]")
                ) {
                    if (transcriptionBasePlayer.isPaused()) {
                        currentSource.classList.add("current-active-word--paused");
                        currentSource.classList.remove("current-active-word");
                    } else {
                        currentSource.classList.add("current-active-word");
                    }
                }
            });
        }
    }, [currentActiveTranscriptionWordID, transcriptData]);
    
    useEffect(() => {
        const currentlyPlaying = getCurrentlyPlayingDocumentID();

        // @ts-ignore
        window.currentlyPlayingSpeakerDocumentID = currentlyPlaying;

        if (!!currentlyPlaying) {
            const activeSpeakerElement = window.document.querySelector(
                `.TranscriptionEditor #speaker-document-${currentlyPlaying}`
            );

            if (
                !!activeSpeakerElement &&
                transcriptionBasePlayer?.isReady()
            ) {
                activeSpeakerElement.classList.add(
                    "transcript-editor-speaker-element--active",
                );
            }
        }
    }, [speakerElements, transcriptTimeDisplay]);

    useEffect(() => {
        const currentSources = window.document.querySelectorAll(
            ".TranscriptionEditor #leaf-" + currentActiveTranscriptionWordID
        );

        const handlePause = () => {
            removePausedHighlighter();

            if(currentSources){
                currentSources?.forEach(currentSource => {
                    if (
                        !!currentSource &&
                        !currentSource.querySelector("span[data-slate-zero-width]")
                    ) {
                        currentSource.classList.add("current-active-word--paused");
                        currentSource.classList.remove("current-active-word");
                    }
                })
            }
        };

        const handlePlay = () => {
            removePausedHighlighter();
        };

        if (transcriptionBasePlayer?.isReady()) {
            transcriptionBasePlayer?.on("playbackPaused", handlePause);
            transcriptionBasePlayer?.on("playbackPlaying", handlePlay);
        }

        return () => {
            if (transcriptionBasePlayer?.isReady()) {
                transcriptionBasePlayer?.off("playbackPaused", handlePause);
                transcriptionBasePlayer?.off("playbackPlaying", handlePlay);
            }
        };
    }, [currentActiveTranscriptionWordID, removePausedHighlighter]);

    useEffect(() => {
        const currentlyPlaying = getCurrentlyPlayingDocumentID();

        const handlePause = () => {
            removeTalkingSpeakerStyle(null);
        };

        const handlePlay = () => {
            if(!!currentlyPlaying) {
                const activeSpeakerElement = window.document.querySelector(
                    `.TranscriptionEditor #speaker-document-${currentlyPlaying}`
                );

                if (!!activeSpeakerElement) {
                    activeSpeakerElement.classList.add(
                        "transcript-editor-speaker-element--talking"
                    );
                }
            }
        };

        if (transcriptionBasePlayer?.isReady()) {
            transcriptionBasePlayer?.on("playbackPaused", handlePause);
            transcriptionBasePlayer?.on("playbackPlaying", handlePlay);
        }

        return () => {
            if (transcriptionBasePlayer?.isReady()) {
                transcriptionBasePlayer?.off("playbackPaused", handlePause);
                transcriptionBasePlayer?.off("playbackPlaying", handlePlay);
            }
        };
    }, [transcriptTimeDisplay, getCurrentlyPlayingDocumentID]);

    useEffect(() => {
        let i = setInterval(() => {
            try {
                //@ts-ignore
                setAudioBridge(window?.bCurrentWordFlag);

                //@ts-ignore
                if (window?.transcriptTimeDisplay !== undefined) {
                    //@ts-ignore
                    setTranscriptTimeDisplay(window?.transcriptTimeDisplay);
                }
                
                //@ts-ignore
                if (window?.transcriptTimeDuration !== undefined) {
                    //@ts-ignore
                    setTranscriptTimeDuration(window?.transcriptTimeDuration);
                }
            }
            catch (e) {

            }
        }, 50);

        return () => clearInterval(i);
    }, [setTranscriptTimeDisplay, setAudioBridge]);

    useEffect(() => {
        siev(transcriptData?.data?.slate || []);
        bridgeTranscriptData(transcriptData?.data?.slate || []);
    }, [transcriptData])

    // Handle error side effects
    useEffect(() => {
         if (isError || (transcriptionValue && !isTranscriptValid(transcriptionValue) && !isRawDeepgramTranscript(transcriptionValue))) {
          setTranscriptHasError(true);
        }
    }, [isError]);

    // Handle real-time PubNub updates
    useEffect(() => {
        if (isTranscriptLoadedFromPubnub) {
            refetch().then(() => {
                setIsTranscriptLoadedFromPubnub(setTranscriptAtom, false);
            });
        }
    }, [isTranscriptLoadedFromPubnub, refetch]);

    const bridgeTranscriptData = (data) => {
        try {
            //@ts-ignore
            window.transcriptBridge = data;
        }
        catch (e) { }
    }

    const isTranscriptValid = (transcriptData) => {
        return (
            transcriptData &&
            transcriptData?.speakerDictionary?.length !== 0 &&
            transcriptData?.speakerLibrary?.length !== 0 &&
            transcriptData?.slate?.length !== 0
        );
    };

    const isRawDeepgramTranscript = (transcriptData) => {
        return transcriptData?.channels && transcriptData?.utterances;
    }

    const handleTranscriptConversion = (transcript) => {
        if (
            isTranscriptValid(transcript) ||
            isRawDeepgramTranscript(transcript)
        ) {
            let existing = false;

            try {
                //@ts-ignore
                if (transcript?.type === "transcript") {
                    setupLibraryAndDictionary(
                        transcript.data.speakerDictionary,
                        transcript.data.speakerLibrary,
                        setInterviewState
                    );

                    setInitialEditorValue((prevValue) =>
                        prevValue?.length <= 0
                            ? transcript?.data?.slate || []
                            : prevValue
                    );

                    bridgeTranscriptData(transcript?.data?.slate || []);
                    setLSaveCache(transcript?.data?.slate || []);
                    setAssemblyStatus(false);
                    existing = true;

                    return transcript;
                }
            } catch (e) {
                appLog(e);
            }

            if (existing === false) {
                generateInitialValue(
                    transcript,
                    (value, buffer, v) => {
                        const data = {
                            speakerDictionary: buffer,
                            speakerLibrary: value,
                            slate: v,
                        };

                        setupLibraryAndDictionary(
                            buffer,
                            value,
                            setInterviewState
                        );

                        updateTranscriptData({
                            transcriptS3ObjectID: props.transcriptDataURL,
                            data: {
                                type: "transcript",
                                data,
                            },
                            onSuccess: () => {
                                client.invalidateQueries({ queryKey: ["fetchTranscriptData"]})
                                setTranscriptData({
                                    type: "transcript",
                                    data,
                                });

                                setupLibraryAndDictionary(
                                    buffer,
                                    value,
                                    setInterviewState
                                );

                                setInitialEditorValue((prevValue) =>
                                    prevValue?.length <= 0 ? v || [] : prevValue
                                );

                                bridgeTranscriptData(v || []);
                                setLSaveCache(v || []);
                                setAssemblyStatus(false);
                                setIsSaving(false);
                                

                                return {
                                    type: "transcript",
                                    data: {
                                        speakerDictionary: buffer,
                                        speakerLibrary: value,
                                        slate: v,
                                    },
                                };
                            },
                        });
                    }
                );
            }
        }
    };

          const [loadedTranscriptID, setLoadedTranscriptID] = useState<string | null>(null);

// Parse only when valid
const { parsedTranscript, isEmpty } = useMemo(() => {
  if (!transcriptionValue || !transcriptDataURL || loadedTranscriptID === transcriptDataURL) {
    return { parsedTranscript: null, isEmpty: false };
  }

  const parsed = handleTranscriptConversion(transcriptionValue);
  return {
    parsedTranscript: parsed,
    isEmpty: parsed?.data?.slate?.length === 0,
  };
}, [transcriptionValue, transcriptDataURL, loadedTranscriptID]);


useEffect(() => {
  if (parsedTranscript && loadedTranscriptID !== transcriptDataURL) {
    setTranscriptData(parsedTranscript);
    setTranscriptHasError(isEmpty);
    setLoadedTranscriptID(transcriptDataURL);
    setInitialEditorValue(parsedTranscript.data.slate); 
  }
}, [parsedTranscript]);
    useEffect(() => {
        const timeout = setTimeout(() => {
            const a = JSON.stringify(tSaveCache);
            const b = JSON.stringify(lSaveCache);

            if (a !== b) {
                if (a !== undefined) {
                    setLSaveCache(tSaveCache);
                    setTSaveCache(undefined);
                    setIsSaving(true);

                    updateTranscriptData({
                        transcriptS3ObjectID: props.transcriptDataURL,
                        data: {
                            type: "transcript",
                            data: {
                                speakerDictionary: speakerDictionary,
                                speakerLibrary: speakerLibrary,
                                slate: tSaveCache,
                            },
                        },
                        onSuccess: () => {
                            client.invalidateQueries({ queryKey: ["fetchTranscriptData"]})
                            setIsSaving(false);
                        },
                    });
                }
            }
        }, 1000);

        return () => clearTimeout(timeout);
    }, [tSaveCache, lSaveCache, speakerLibrary, speakerDictionary]);

    const storeTranscriptData = (newData) => {
        setTSaveCache(newData);
    }

    const handleChangeSpeakerMapping = (documentID: string, newSpeakerID: number, newSpeakerName: string) => {
        const updatedSpeakerLibrary = [];
        const updatedSpeakerDictionary = [];
        let anySpeakerNodeMatches = false;
        let anySpeakerMatches = false;

        each(speakerLibrary, speakerNode => {
            const updatedSpeakerNode = { ...speakerNode };

            if (speakerNode.documentID === documentID) {
                updatedSpeakerNode.speakerID = newSpeakerID;
                anySpeakerNodeMatches = true;
            }

            updatedSpeakerLibrary.push(updatedSpeakerNode);
        });

        each(speakerDictionary, speaker => {
            const updatedSpeaker = { ...speaker };

            if (speaker.id === newSpeakerID) {
                updatedSpeaker.name = newSpeakerName;
                anySpeakerMatches = true;
            }

            updatedSpeakerDictionary.push(updatedSpeaker);
        });

        if (!anySpeakerNodeMatches) {
            updatedSpeakerLibrary.push({
                speakerID: newSpeakerID,
                documentID: documentID
            })
        }

        if (!anySpeakerMatches) {
            updatedSpeakerDictionary.push({
                id: newSpeakerID,
                name: newSpeakerName
            })
        }

        setupLibraryAndDictionary(
            updatedSpeakerDictionary,
            updatedSpeakerLibrary,
            setInterviewState
        );

        setIsSaving(true);

        updateTranscriptData({
            transcriptS3ObjectID: props.transcriptDataURL,
            data: {
                type: "transcript",
                data: {
                    speakerDictionary: updatedSpeakerDictionary,
                    speakerLibrary: updatedSpeakerLibrary,
                    slate: tSaveCache,
                },
            },
            onSuccess: () => {
                client.invalidateQueries({ queryKey: ["fetchTranscriptData"]})
            },
            onFinish: () => {
                setIsSaving(false);
            },
        });
    }

    const handleRemoveSpeaker = (speakerID) => {
        removeNodeByTypeAndPerson(editor, "speaker", speakerID);
    }

    const handleInsertNewSpeaker = (
        selectedName,
        request,
        newlyAddedSpeaker = false
    ) => {
        const newDocumentID = request?.id;
        const insertSolution = request?.insertSolution;
        let foundSpeakerID = speakerDictionary[0]?.id;

        if (newlyAddedSpeaker) {
            foundSpeakerID = selectedName;
        } else {
            for (let i = 0; i < speakerDictionary?.length; i++) {
                if (selectedName === speakerDictionary[i]?.name) {
                    foundSpeakerID = speakerDictionary[i].id;
                }
            }
        }

        // insert this into the speaker library
        let newSpeakerLibrary = [...speakerLibrary];

        newSpeakerLibrary.push({
            documentID: newDocumentID,
            speakerID: foundSpeakerID,
        });

        // update our speaker library to have the new entry
        setupLibrary(newSpeakerLibrary, setInterviewState);

        editor.setSelection(insertSolution);

        const blockUnderCursor = getBlockFromLocation(
            editor,
            insertSolution.anchor
        );

        Transforms.insertNodes(editor, {
            // @ts-ignore
            type: "speaker",
            person: newDocumentID,
            time: blockUnderCursor.timeStamp,
            children: [{ text: "" }],
        });

        editor.setSelection(insertSolution);
    };

    const handlePublishSpeakerChange = (
        documentID,
        newSpeaker,
        newlyAddedSpeaker = false
    ) => {
        if (!!newSpeaker) {
            if (!documentID && !!newSpeakerRequest) {
                handleInsertNewSpeaker(
                    newSpeaker.label,
                    newSpeakerRequest,
                    newlyAddedSpeaker
                );
            } else {
                handleChangeSpeakerMapping(
                    documentID,
                    newSpeaker.id,
                    newSpeaker.label
                );
            }
        }
    };

    const getListOfSpeakers = () => {
        let listOfSpeakers = [];

        // Match any entries where the start of any "seperate" word
        // begins with the given input
        each(speakerLibrary, speaker => {
            try {
                let words = speaker.name?.split(' ');
                let anyWordsFound = false;

                each(words, subWord => {
                    const subWordLower = subWord.toLowerCase();
                    const wordToMatchLower = (speakerRequest ? speakerRequest.userInput : "").toLowerCase();

                    if (subWordLower.startsWith(wordToMatchLower)) {
                        anyWordsFound = true;
                    }
                })

                if (anyWordsFound) {
                    listOfSpeakers.push(speaker.name);
                }
            }
            catch (e) {
                console.error(e);
            }
        });

        return listOfSpeakers;
    }

    const renderFloatingSpeakerRequest = () => {
        return (
            <Popover
                open={true}
                anchorEl={newSpeakerRequest.el}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                onClose={() => {
                    setNewSpeakerRequest(undefined);
                }}
            >
                <Box
                    sx={{
                        border: `2px solid ${theme.custom.mainButtonHighlight}`,
                        borderRadius: "4px",
                        ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                            minWidth: "110px",
                        },
                    }}
                >
                    <MaterialSpeakerPopup
                        onChange={(newValue) => {
                            if (newValue !== undefined) {
                                handlePublishSpeakerChange(
                                    undefined,
                                    newValue,
                                    true
                                );
                            }
                        }}
                        onClose={() => {
                            setNewSpeakerRequest(undefined);
                        }}
                        transcriptDataURL={transcriptDataURL}
                        slateData={transcriptData?.data?.slate}
                        speakerDictionary={speakerDictionary}
                        speakerLibrary={speakerLibrary}
                        suggestions={getListOfSpeakers()}
                        placeholder="Select Speaker"
                    />
                </Box>
            </Popover>
        );
    }

    const renderInsightElement = (person, children, time, e) => {
        return <div>{children}</div>
    }

    const renderSpeakerElement = (documentID, children, time, e) => (
        <TranscriptSpeakerElement
            documentID={documentID}
            children={children}
            time={time}
            isReadOnly={isReadOnly}
            speakerDictionary={speakerDictionary}
            speakerLibrary={speakerLibrary}
            speaker={getSpeakerFromDocID(documentID)}
            handlePublishSpeakerChange={handlePublishSpeakerChange}
            handleRemoveSpeaker={handleRemoveSpeaker}
            onPlayPaus={() => {
                // @ts-ignore
                const currentlyPlayingSpeakerDocumentID = window.currentlyPlayingSpeakerDocumentID;

                if (!!transcriptionBasePlayer && transcriptionBasePlayer?.isReady()) {
                    if (transcriptionBasePlayer?.isPaused()) {
                        if (currentlyPlayingSpeakerDocumentID !== documentID) {
                            transcriptionBasePlayer?.seek(time);
                        }
                        
                        transcriptionBasePlayer?.play();
                    } else {
                        if (currentlyPlayingSpeakerDocumentID !== documentID) {
                            transcriptionBasePlayer?.seek(time);
                            transcriptionBasePlayer?.play();
                        } else {
                            transcriptionBasePlayer?.pause();
                        }
                    }
                }
            }}
        />
    );

    const renderElement = ({ attributes, children, element }) => {
        switch (element.type) {
            case 'insight':
                return <div {...attributes}>{renderInsightElement(undefined, undefined, undefined, undefined)}</div>
            case 'speaker':
                // https://github.com/ianstormtaylor/slate/issues/3421
                return <div {...attributes} contentEditable={false} style={{ userSelect: "none", content: '""' }}>{renderSpeakerElement(element.person, children, element.time, element)}</div>
            default:
                let readOnlyStyle = {
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: "black !important",
                    },
                    padding: "0 0 24px 16px",
                };

                let editStyle = {
                    cursor: "text",
                    padding: "0 0 24px 16px",
                };

                return <p style={isReadOnly ? readOnlyStyle : editStyle} {...attributes}>{children}</p>
        }
    };

    const removeInsightHighlighter = () => {
        const highlightedWords = window.document.querySelectorAll(
            ".TranscriptionEditor .selected-insight-word, .TranscriptionEditor .selected-insight-word--first, .TranscriptionEditor .selected-insight-word--last"
        );

        if(highlightedWords){
            highlightedWords?.forEach((word) => {
                word.classList.remove("selected-insight-word");
                word.classList.remove("selected-insight-word--first");
                word.classList.remove("selected-insight-word--last");
            });
        }
        
        highlightedWords?.forEach((word) => {
            word.classList.remove("selected-insight-word");
            word.classList.remove("selected-insight-word--first");
            word.classList.remove("selected-insight-word--last");
        });
    };

    const highlightInsight = (insightID) => {
        removeInsightHighlighter();

        if (insightID) {
            const insightWords = window.document.querySelectorAll(
                `.TranscriptionEditor [data-insight-id="${insightID}"]`
            );

            insightWords?.forEach((word, index) => {
                word.classList.add("selected-insight-word");
                
                if (index === 0) {
                    word.classList.add("selected-insight-word--first");
                } else if (index === insightWords.length - 1) {
                    word.classList.add("selected-insight-word--last");
                }
            });
        }
    }

    const navigateToInsight = (insightID) => {
        const firstWordOfInsight = window.document.querySelector(
            `.TranscriptionEditor [data-insight-id="${insightID}"]`
        );

        if (!!firstWordOfInsight) {
            firstWordOfInsight.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        if (analysis !== undefined && analysis !== true && analysis.attemptCount !== undefined) {
            if (analysis.result) {
                const rid = analysis.result.requestId;

                axios.get(`https://insights.innovationwithin.services/status?requestId=${rid}`, {
                    withCredentials: false
                }).then((res2) => {
                    setTimeout(() => {
                        const insightsAvailable = res2.data.result.insightsAvailable === 1;

                        if (insightsAvailable === false) {
                            setAnalysis({
                                ...res2.data,
                                result: {
                                    requestId: rid
                                },
                                attemptCount: analysis.attemptCount += 1
                            });
                        }
                        else {
                            setAnalysis({ ...res2.data.result });
                        }
                    }, 15000);

                });
            }
        }
    }, [analysis])

    useEffect(() => {
        let lastFoundFlag: any = undefined;

        if (!!initialEditorValue) {
            for (let i = 0; i < initialEditorValue?.length; i++) {
                const z = initialEditorValue[i];
                if (z !== undefined && z.type !== undefined) {
                    if (z.type === "paragraph") {
                        for (let c = 0; c < z?.children?.length; c++) {
                            const x = z.children[c];

                            const t = parseFloat(x.timeStamp);

                            if (t < audioTime) {
                                lastFoundFlag = x.id;
                            }
                            else {
                                break;
                            }
                        }
                    }
                }
            }
        }

        try {
            //@ts-ignore
            window.cacheAudioLeafFlag = `leaf-${lastFoundFlag}`;
        }
        catch (e) { }

        setAudioLeafFlag(lastFoundFlag);
    }, [initialEditorValue, audioTime]);

    const renderLeaf = useCallback(
        ({ attributes, children, leaf }) => {
            const style: CSSProperties = {
                fontWeight: leaf.bold ? "bold" : "normal",
                fontStyle: leaf.italic ? "italic" : "normal",
                cursor: isReadOnly ? "pointer" : "text",
            };

            if (leaf.id === undefined) {
                return (
                    <span style={{ pointerEvents: "none", userSelect: "none" }}></span>
                );
            }

            if (!!leaf.insight && leaf?.id !== window.bCurrentWordFlag) {
                style.background = leaf?.insightBackground?.[0];
                style.color = leaf?.insightColor?.[0];
            }

            const isSelected =
                Array.isArray(leaf.insightID) &&
                base?.selectedFinding?.findingID &&
                leaf.insightID.includes(base.selectedFinding.findingID);

            // @ts-ignore
            if (!!leaf.insight && isSelected && leaf?.id !== window.bCurrentWordFlag) {
                
                const index = leaf.insightID.findIndex(
                    (id) => id === base.selectedFinding.findingID
                );
                
                const bgColor = leaf.insightBackground?.[index] || leaf.insightBackground?.[0];
                const color = leaf.insightColor?.[index] || leaf.insightColor?.[0];
                style.background = bgColor;
                style.color = color;
            }

            

            if (leaf?.highlight) {
                style.background = theme.custom.textSelectionColor;
                style.color = theme.custom.text;
            }

            return (
                <span
                    {...attributes}
                    style={style}
                    id={`leaf-${leaf.id}`}
                    data-insight-id={isSelected ? base?.selectedFinding?.findingID : ""}
                    className={`transcript-leaf-word ${
                        // @ts-ignore
                        leaf?.id === window.bCurrentWordFlag ? "current-active-word--paused" : ""
                    } ${leaf.highlight ? "transcript-leaf-word--highlighted" : ""} ${
                        leaf.insight ? "transcript-leaf-word--insight" : ""
                    }`}
                    timeStamp={leaf.timeStamp}
                    onClick={() => {
                        if (isReadOnly) {
                            if (leaf && leaf.timeStamp !== undefined) {
                                if (!props.editingNote) {
                                    try {
                                        //@ts-ignore
                                        transcriptionBasePlayer?.seek(leaf.timeStamp);
                                        //@ts-ignore
                                        transcriptionBasePlayer?.play();
                                    } catch (e) {}
                                }
                            }
                        }
                    }}
                >
                    {children}
                </span>
            );
        },
        [
            isReadOnly,
            audioLeafFlag,
            interviewState?.placeholderFinding,
            findings,
            interviewState?.findings,
            interviewState?.selectedFinding,
            base.selectedFinding,
            base.insightSelection,
        ]
    );

    useEffect(() => {
        const selectedInsightID = base?.selectedFinding?.findingID;

        highlightInsight(selectedInsightID);
        navigateToInsight(selectedInsightID);
    }, [base?.selectedFinding?.findingID,isDecorateComplete]);

   const [isFetchFindingForTranscript, setIsFetchFindingForTranscript] = useState(false)

  useGetFindingArtifact(
     { accessToken: keycloak.token },
     { artifactID: transcriptDataURL },
     { enabled: isFetchFindingForTranscript }
   );

    useEffect(() => {
        if (migrationState?.isTranscriptMigrated) {
            // fetchFindingsForTranscript();
            setIsFetchFindingForTranscript(true)
        }
    }, [migrationState?.isTranscriptMigrated]);

    const getLength = token => {
        if (typeof token === 'string') {
            return token.length
        } else if (typeof token.content === 'string') {
            return token.content.length
        } else {
            return token.content.reduce((l, t) => l + getLength(t), 0)
        }
    }

    function getCompliantTextColor(bgColor) {
        // Convert hex color to RGB
        const r = parseInt(bgColor.slice(1, 3), 16);
        const g = parseInt(bgColor.slice(3, 5), 16);
        const b = parseInt(bgColor.slice(5, 7), 16);

        // Calculate brightness
        const brightness = (r * 0.299) + (g * 0.587) + (b * 0.114);

        // Return black (#000000) or white (#FFFFFF) text color based on brightness
        return brightness > 128 ? '#000000' : '#FFFFFF';
    }

    const extractColorProfileFromFinding = (finding) => {
        let topCustomID = undefined;

        for (let i = 0; i < finding?.joinery?.length; i++) {
            if (topCustomID === undefined) {
                topCustomID = finding?.joinery[i]?.customFindingTypeID;
            }
        }

        if (topCustomID === undefined) {
            return {
                color: getCompliantTextColor(theme.custom.disabledColor),
                backgroundColor: theme.custom.disabledColor,
            };
        }

        let backgroundColor = theme.custom.disabledColor;

        for (let i = 0; i < customFindingTypes?.length; i++) {
            if (
                customFindingTypes[i].customFindingTypeID === topCustomID
            ) {
                backgroundColor =
                    customFindingTypes[i].color || backgroundColor;
            }
        }

        return {
            backgroundColor,
            color: getCompliantTextColor(backgroundColor),
        };
    };

    useEffect(() => {
      if (initialEditorValue?.length > 0) {
        setIsDecorateComplete(true);
      }
    }, [initialEditorValue]);

    const insightRangeMap = useMemo(() => {
      const map = new Map<string, any[]>();

      const interviewIDs = interviewFindings?.[base.selectedInterview] || [];

      for (const findingID of interviewIDs) {
        const finding = findings?.[findingID];
        if (!finding?.quote) continue;

        try {
          const parsedQuote = JSON.parse(finding.quote);
          if (
            parsedQuote.source !== "transcript" ||
            !parsedQuote.selection?.anchor || 
            !parsedQuote.selection?.focus
          )
            continue;

          const fullRange = Editor.range(
            editor,
            parsedQuote.selection.anchor,
            parsedQuote.selection.focus
          );
          
          const profile = extractColorProfileFromFinding(finding);

          const startPath = fullRange?.anchor?.path?.[0];
          const endPath = fullRange.focus?.path?.[0];

          for (
            let path = startPath;
            path <= endPath;
            path++
          ) {
            const key = path?.toString();
            const arr = map?.get(key) || [];
            arr.push({
              range: fullRange,
              insightID: findingID,
              insightBackground: profile.backgroundColor,
              insightColor: profile.color,
            });
            map.set(key, arr);
          }
        } catch (e) {
        //   console.error("Failed parsing finding", findingID, e);
        }
      }

      return map;
    }, [
      base?.selectedInterview,
      Object?.keys(findings || {}),
      interviewFindings?.length,
    ]);

    const isValidPoint = (point) =>
      point &&
      Array.isArray(point.path) &&
      typeof point.offset === "number" &&
      point.path.every((n) => typeof n === "number");

    const isValidPath = (path, editor) => {
      try {
        Node.get(editor, path);
        return true;
      } catch {
        return false;
      }
    };

    const isValidRange = (range, editor) =>
      range &&
      isValidPoint(range.anchor) &&
      isValidPoint(range.focus) &&
      isValidPath(range.anchor.path, editor) &&
      isValidPath(range.focus.path, editor);

   const decorate = useCallback(
      ([node, path]) => {
        const ranges = [];

        if (!Text?.isText(node)) return ranges;

        const key = path?.[0]?.toString();
        const matches = insightRangeMap?.get(key) || [];

        let textNodeRange;
        try {
          textNodeRange = Editor.range(editor, path);
        } catch {
          textNodeRange = null;
        }

        if (!isValidRange(textNodeRange, editor)) {
          return [];
        }

        const pathCache = {};

        const isValidPathMemo = (path) => {
          const key = path.join(".");
          if (key in pathCache) return pathCache[key];

          try {
            Node.get(editor, path);
            pathCache[key] = true;
            return true;
          } catch {
            pathCache[key] = false;
            return false;
          }
        };

        const isValidRangeFast = (range) =>
          isValidPoint(range.anchor) &&
          isValidPoint(range.focus) &&
          isValidPathMemo(range.anchor.path) &&
          isValidPathMemo(range.focus.path);

        for (const match of matches) {
          if (
            !match ||
            !match.range ||
            !match.insightID ||
            !match.insightColor ||
            !match.insightBackground
          ) {
            continue;
          }

          const matchRange = match.range;

          if (!isValidRangeFast(matchRange)) continue;

          let intersection;
          try {
            intersection = Range.intersection(matchRange, textNodeRange);
          } catch (e) {
            intersection = null;
          }

          if (intersection && !Range.isCollapsed(intersection)) {
            const existing = ranges.find((r) => Range.equals(r, intersection));
            
            if (existing) {
              if (!existing.insightID?.includes(match.insightID)) {
                existing.insightID?.push(match.insightID);
                existing.insightColor?.push(match.insightColor);
                existing.insightBackground?.push(match.insightBackground);
              }
            } else {
              ranges.push({
                ...intersection,
                insight: true,
                insightID: [match.insightID],
                insightColor: [match.insightColor],
                insightBackground: [match.insightBackground],
              });
            }
          }
        }

        if (lastSeenSelection?.anchor && lastSeenSelection?.focus) {
          let seenIntersection;
          try {
            seenIntersection = Range.intersection(
              Editor.range(
                editor,
                lastSeenSelection?.anchor,
                lastSeenSelection?.focus
              ),
              textNodeRange
            );
          } catch {
            seenIntersection = null;
          }
          if (seenIntersection) {
            ranges.push({ ...seenIntersection, highlight: true });
          }
        }

        // if (base.insightSelection) {
        //   let insightSelectionIntersection;
        //   try {
        //     insightSelectionIntersection = Range?.intersection(
        //       base?.insightSelection,
        //       textNodeRange
        //     );
        //   } catch {
        //     insightSelectionIntersection = null;
        //   }

        //   if (insightSelectionIntersection) {
        //     ranges.push(insightSelectionIntersection);
        //   }
        // }

        return ranges;
      },
      [
        insightRangeMap,
        lastSeenSelection?.anchor,
        lastSeenSelection?.focus,
        // base?.insightSelection,
      ]
    );


    const getLeafRect = (id) => {
      try {
        return window.document
          .querySelector(".TranscriptionEditor #leaf-" + id)
          .getClientRects()[0];
      } catch (e) {
        return {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        };
      }
    };

    const getEl = (id) => {
        try {
            return window.document.querySelector(".TranscriptionEditor #leaf-" + id);
        }
        catch (e) {
            return undefined;
        }
    }

    const hasAnyBlocksOtherThenSpeaker = (data) => {
        let anyNonSpeakerBlocksFound = false;

        try {
            for (let i = 0; i < data?.length; i++) {
                if (data[i].type !== "speaker") {
                    anyNonSpeakerBlocksFound = true;
                }
            }
        }
        catch (e) { }

        if (anyNonSpeakerBlocksFound) {
            return true;
        }

        return false;
    }

    // Function to remove a node based on the type and the 'person' property
    const removeNodeByTypeAndPerson = (editor, nodeType, personValue) => {
        // Define a path to keep track of where in the document we are
        for (let i = 0; i < editor?.children?.length; i++) {
          if (editor.children[i].person === personValue) {
            if (Editor.hasPath(editor, [i])) {
              const firstNode = Node.get(editor, [i]);
              if (firstNode) {
                Transforms.removeNodes(editor, { at: [i] });
              }
            }
          }
        }
    };

    const clearEditorSelection = () => {
        // @ts-ignore
        window.__lastInsightSelection = undefined;
        setLastSeenSelection(undefined);
        setShowEditorClickBlocker(false);
    }

    const editorReady = useMemo(() => {
      return (
        Array.isArray(initialEditorValue) &&
        initialEditorValue.length > 0 &&
        showPlayers
      );
    }, [initialEditorValue, showPlayers]);

    const memoizedEditor = useMemo(() => {
        if (!editorReady) {
            return <FetchingTranscriptLoader theme={theme} noBorder={true}/>
        }

        let safeEditorValue = initialEditorValue;
        let dataNotActuallyReady = false;

        if (
            JSON.stringify(safeEditorValue) === JSON.stringify({}) ||
            safeEditorValue === undefined
        ) {
            safeEditorValue = [];
            dataNotActuallyReady = true;
        }

        if (safeEditorValue === undefined) {
            dataNotActuallyReady = true;
        }
        else {
            if (Array.isArray(safeEditorValue) && safeEditorValue?.length === 0) {
                dataNotActuallyReady = true;
            }
        }

        if (!transcriptHasError && dataNotActuallyReady) {
            return (
                <Stack>
                    <Alert
                        severity="info"
                        sx={{ border: `1px solid ${theme.custom.borderGrey}` }}
                    >
                        <Stack spacing={2}>
                            <AlertTitle>Transcribing Interview</AlertTitle>
                            <Typography sx={{ fontWeight: 400 }}>
                                Feel free to continue to use the platform, we'll notify
                                you when the transcription is ready.
                            </Typography>
                            <LinearProgress />
                        </Stack>
                    </Alert>
                </Stack>
            );
        }
        else {
            // so we have data, but is it corrupt?
            if (transcriptHasError || !hasAnyBlocksOtherThenSpeaker(safeEditorValue)) {
                return (
                    <Stack>
                        <Alert
                            severity="error"
                            sx={{ border: `1px solid ${theme.custom.borderGrey}` }}
                        >
                            <AlertTitle>
                                Error Transcribing Interview
                            </AlertTitle>
                            <Typography>
                                This audio file couldn't be transcribed. Sorry
                                for the inconvience.
                            </Typography>
                        </Alert>
                    </Stack>
                );
            }
        }

        // only render when we have a video attached
        const type = getAssemblyTypeFromInterview(props.assemblies, base.selectedInterview);

  
        const calculatedTranscriptHeightSub =
       // type === "video" ? base.discoveryFocusMode ?"507px":"456px" : base.discoveryFocusMode ? "291px":"240px";
            type === "video" ? "452px" : "236px";
        const selectedTextNodes = getSelectedTextNodes(editor);

        // This block will refresh the editor and will set the
        // New transcription state to the editor
        // Otherwise, speaker names can't be displayed after retranscription
        if (
            editor.children?.length > 0 &&
            // @ts-ignore
            editor.children?.[0]?.person !== safeEditorValue?.[0]?.person
        ) {
            Transforms.delete(editor, {
                at: {
                    anchor: Editor.start(editor, []),
                    focus: Editor.end(editor, []),
                },
            });

            // // Removes empty node
            // Transforms.removeNodes(editor, {
            //     at: [0],
            // });
            if (Editor.hasPath(editor, [0])) {
                const firstNode = Node.get(editor, [0]);
                if (firstNode) {
                  Transforms.removeNodes(editor, { at: [0] });
                }
            }

            // Insert array of children nodes
            Transforms.insertNodes(editor, safeEditorValue);
        }

        return (
            <Stack sx={{ position: "relative" }}>
                {showEditorClickBlocker && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: "100%",
                            zIndex: 1,
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            clearEditorSelection();
                            window.getSelection()?.removeAllRanges();
                        }}
                    />
                )}
                {isFetchingTranscriptionData && <LinearProgress />}
                <Slate
                    key={`${base?.selectedInterview}-${transcriptDataURL}`}
                    onChange={(value) => {
                        if(isEditing){
                            storeTranscriptData(value);
                            bridgeTranscriptData(value);
                        }
                    }}
                    editor={editor}
                    initialValue={safeEditorValue}
                >
                    <SelectionMonitor
                        onSet={(range) => {
                            setLastSeenSelection(range);
                        }}
                    />
                        <style>
                        { `
                            .TranscriptionEditor *::selection {
                                background: rgb(151, 207, 224) !important;
                                color: inherit !important;
                            }
                        `}
                    </style>

                    <Editable
                        className="TranscriptionEditor"
                        spellCheck={false}
                        decorate={decorate}
                        autoFocus={!isReadOnly}
                        renderLeaf={renderLeaf}
                        renderElement={renderElement}
                        readOnly={isReadOnly}
                        onClick={(e) => {
                            if (!getSelectedText(selectedTextNodes)) {
                                clearEditorSelection();
                            }
                        }}
                        onPointerUp={() => {
                            setShowEditorClickBlocker(
                                isReadOnly && !!getSelectedText(selectedTextNodes)
                            );
                        }}
                        style={{
                            width: "100%",
                            wordBreak: "keep-all",
                            userSelect: "text",
                            padding: "12px",
                            height: `calc(100vh - ${appHeaderHeight} - ${calculatedTranscriptHeightSub} - ${transcriptSectionHeaderHeight} + 96px)`,
                            overflowX: "visible",
                            overflowY: "scroll",
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Escape") {
                                clearEditorSelection();
                                ReactEditor.blur(editor);
                            } else if (event.key === "@") {
                                // Prevent the ampersand character from being inserted.
                                event.preventDefault();

                                // Use this as the
                                const blockUnderCursor = getBlockFromLocation(
                                    editor,
                                    editor.selection.anchor
                                );

                                if (
                                    blockUnderCursor !== undefined &&
                                    blockUnderCursor.id !== undefined
                                ) {
                                    setNewSpeakerRequest({
                                        ...blockUnderCursor,
                                        rect: getLeafRect(blockUnderCursor.id),
                                        el: getEl(blockUnderCursor.id),
                                        insertSolution: editor.selection,
                                        userInput: "",
                                    });
                                }
                            }
                        }}
                    />
                </Slate>
            </Stack>
        );
    }, [
        initialEditorValue,
        interviewState,
        isReadOnly,
        base.insightSelection,
        base.selectedInterview,
        findings,
        lastSeenSelection,
        selectedSpeaker,
        isSaving,
        showEditorClickBlocker,
        base.selectedFinding,
        transcriptHasError,
        base.discoveryFocusMode,
        editorReady
    ]);

    const newSpeakerPopup = useMemo(() => {
        const open = newSpeakerRequest !== undefined;

        if (open) {
            return renderFloatingSpeakerRequest()
        }
        else {
            return <span></span>
        }
    }, [newSpeakerRequest]);

    if (listenWait) {
        return <MaterialTranscriptionAudioPlayer assemblies={props?.assemblies} listenWait={(t) => {
            setListenWait(t)
        }} duration={transcriptTimeDuration} audioTime={transcriptTimeDisplay} onSeek={(e) => {
            setAudioTime(e);
        }} />
    }

    const retranscribeMedia = () => {
        setIsReTranscribing(true);
        const interviewID = base?.selectedInterview;
        protectedApiCall(
            discoverySession,
            "interview/retranscribe",
            {
                interviewID,
            },
            {},
            "put"
        )
            .then((res) => {
                setRetrancriptionProgressByInterviewID(
                    setRetranscriptionProgress,
                    interviewID,
                    true
                );
            })
            .finally(() => setIsReTranscribing(false));
    };

    const renderTranscribeAudioSection = () => {
        return (
            <Stack>
                <Dialog
                    className="confirmation-dialog"
                    open={showTranscriptConfirmationModal}
                    sx={{ zIndex: theme.custom.dialogZIndex }}
                    onClose={() => {
                        setShowTranscriptConfirmationModal(false);
                    }}
                >
                    <DialogTitle>Missing Transcript</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            This interview does not have a transcript. Do you
                            want to transcribe it?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ margin: "0px 16px 8px 0px" }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setShowTranscriptConfirmationModal(false);
                            }}
                        >
                            No
                        </Button>
                        <Button
                            variant="outlined"
                            autoFocus
                            onClick={() => {
                                retranscribeMedia();
                                setShowTranscriptConfirmationModal(false);
                            }}
                        >
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
                <Alert
                    severity="info"
                    sx={{ border: `1px solid ${theme.custom.borderGrey}` }}
                >
                    <Stack spacing={2}>
                        <AlertTitle>Media is not transcribed</AlertTitle>
                        <Typography>
                            You have a media which is not transcribed. Do you
                            want to transcribe it?
                        </Typography>
                        <IwButton
                            variant="contained"
                            color="primary"
                            loading={isReTranscribing}
                            disabled={isReTranscribing}
                            onClick={retranscribeMedia}
                        >
                            {isReTranscribing
                                ? "Transcribing..."
                                : "Transcribe Media"}
                        </IwButton>
                    </Stack>
                </Alert>
            </Stack>
        );
    };

    const renderStatusOrEditor = () => {
        if (!props.hasTranscript) {
            return renderTranscribeAudioSection();
        }

        if (transcriptHasError) {
            return (
                <Stack>
                    <Alert
                        severity="error"
                        sx={{ border: `1px solid ${theme.custom.borderGrey}` }}
                    >
                        <Stack spacing={2}>
                            <AlertTitle>
                                Error Transcribing Interview
                            </AlertTitle>
                            <Typography>
                                This audio file couldn't be transcribed. Sorry
                                for the inconvience.
                            </Typography>
                        </Stack>
                    </Alert>
                </Stack>
            );
        }

        if (
            assemblyStatus === false &&
            !reTranscriptionProgress?.[base?.selectedInterview]
        ) {
            return memoizedEditor 
        }

        return <FetchingTranscriptLoader theme={theme}/>
    }

    return (
      <div
        ref={outerEditorPortal}
        style={{ position: "relative", top: "-5px", width: "100%" }}
      >
        {newSpeakerPopup}
        <Stack
          id="transcript-parent"
          sx={{
            width: "100%",
            backgroundColor: "transparent",
            borderRadius: "4px",
            boxShadow: isEditing
              ? `0 0 0 2px ${theme.custom.mainButtonHighlight}`
              : `0 0 0 1px ${theme.custom.borderGrey}`,
            transition: "box-shadow 0.2s ease",
            ".TranscriptionEditor *": {
              "::selection": {
                background: "transparent",
              },
            },
          }}
        >
          <TranscriptionEditorHeader
            sx={{
              height: transcriptSectionHeaderHeight,
            }}
            isEditing={isEditing}
            isSaving={isSaving}
            slateData={transcriptData?.data?.slate}
            transcriptDataURL={props.transcriptDataURL}
            onEditEnd={() => {
              setIsEditing(!isEditing);
            }}
            onLocateActiveWord={() => {
              navigateToActiveWord();
            }}
          />

          {renderVideoBlock(props.assemblies, base?.selectedInterview)}
          <MaterialTranscriptionAudioPlayer
            listenWait={(t) => {
              setListenWait(t);
            }}
            duration={transcriptTimeDuration}
            audioTime={transcriptTimeDisplay}
            onSeek={(e) => {
              setAudioTime(e);
            }}
          />
          {renderStatusOrEditor()}
        </Stack>
      </div>
    );
}
